import { useEffect, useState } from "react";
import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import MacrosSummary from "@/components/MacrosSummary";
import { Clock, CaretLeft, StarFour, FilePdf, BookOpen, Barbell } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { exportPlanPdf } from "@/lib/exportPdf";
import { exportRegolamentoPdf } from "@/lib/exportRegolamento";
import { computeBmiTdee } from "@/lib/bmiTdee";

export function ClientPlansList() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [studio, setStudio] = useState(null);
  useEffect(() => {
    if (!user) return;
    api.get(`/clients/${user.user_id}/plans`).then(({ data }) => setPlans(data));
    api.get("/studio-settings").then(({ data }) => setStudio(data)).catch((err) => { if (process.env.NODE_ENV !== "production") console.error("[client-plans] studio fetch failed:", err); });
  }, [user]);

  return (
    <div data-testid="client-plans">
      <div className="label-tiny">Storico</div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h1 className="font-serif text-5xl tracking-tight mt-2">Piani nutrizionali</h1>
        <Button
          data-testid="download-regolamento-btn"
          variant="outline"
          onClick={() => exportRegolamentoPdf({ studio })}
          className="rounded-full mt-2 border-2"
        >
          <BookOpen size={14} weight="duotone" className="mr-1.5" /> Regolamento (PDF)
        </Button>
      </div>
      <p className="mt-3 text-sm text-[color:var(--text-secondary)] max-w-lg">
        Scarica il regolamento generale con tutte le buone regole del percorso nutrizionale.
      </p>
      <div className="mt-8 space-y-3">
        {plans.length === 0 && <div className="text-sm text-[color:var(--text-secondary)]">Nessun piano ancora.</div>}
        {plans.map((p) => (
          <Link key={p.plan_id} to={`/piani/${p.plan_id}`} className="flex items-center justify-between border border-[color:var(--border)] rounded-2xl p-5 hover:-translate-y-1 transition-transform bg-[color:var(--bg-primary)]" style={{ transition: "transform 0.2s ease" }}>
            <div>
              <div className="flex items-center gap-2">
                <div className="font-serif text-xl">{p.name}</div>
                {p.active && <span className="text-xs px-2.5 py-0.5 rounded-full bg-[color:var(--accent-2)] text-white inline-flex items-center gap-1"><StarFour size={12} weight="fill" /> Attivo</span>}
              </div>
              <div className="text-xs text-[color:var(--text-secondary)] mt-1">
                {p.totals?.calories || 0} kcal · {(p.meals?.length || 0)} pasti · dal {(p.created_at || "").slice(0, 10)}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function ClientPlanDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [foodDb, setFoodDb] = useState(null);
  const [latestMeasurement, setLatestMeasurement] = useState(null);
  const [studio, setStudio] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/plans/${id}`);
      setPlan(data);
      const { data: fdb } = await api.get(`/nutrition/food-db?gender=${user?.gender || "M"}`);
      setFoodDb(fdb);
      if (user?.user_id) {
        try {
          const { data: measurements } = await api.get(`/clients/${user.user_id}/measurements`);
          const sorted = [...measurements].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
          setLatestMeasurement(sorted[0] || null);
        } catch (err) { if (process.env.NODE_ENV !== "production") console.error("[client-plans] measurements fetch failed:", err); }
      }
    })();
    api.get("/studio-settings").then(({ data }) => setStudio(data)).catch(() => {});
  }, [id, user]);

  const proteineNobili = React.useMemo(() => {
    if (!foodDb || !plan) return 0;
    const totProt = (plan.meals || []).reduce((s, m) => s + Number(m.portions?.proteici || 0), 0);
    const perPortion = foodDb.avg_per_portion?.proteici?.protein_g || 0;
    return +(totProt * perPortion).toFixed(1);
  }, [plan, foodDb]);

  if (!plan) return <div className="text-sm">Caricamento…</div>;

  return (
    <div data-testid="client-plan-detail">
      <Link to="/piani" className="inline-flex items-center gap-1 label-tiny hover:text-[color:var(--accent)]">
        <CaretLeft size={14} weight="bold" /> Piani
      </Link>
      <div className="mt-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-tiny">Piano nutrizionale</div>
          <h1 className="font-serif text-4xl tracking-tight mt-1">{plan.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          {plan.active && (
            <span className="text-xs px-3 py-1 rounded-full bg-[color:var(--accent-2)] text-white inline-flex items-center gap-1">
              <StarFour size={12} weight="fill" /> Piano attivo
            </span>
          )}
          <Button
            data-testid="client-export-pdf-btn"
            variant="outline"
            onClick={() => exportPlanPdf({
              plan,
              client: user,
              totals: plan.totals || {},
              foodDb,
              massaMagraKg: latestMeasurement?.massa_magra_kg || 0,
              proteineNobiliG: proteineNobili,
              tdee: computeBmiTdee({
                pesoKg: latestMeasurement?.peso_kg || 0,
                heightCm: user?.height_cm || 0,
                massaMagraKg: latestMeasurement?.massa_magra_kg || 0,
                activityLevel: user?.activity_level || null,
              }).tdee,
            })}
            className="rounded-full"
          >
            <FilePdf size={14} weight="duotone" className="mr-1" /> Esporta PDF
          </Button>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-[minmax(0,1fr)_360px] gap-8">
        <div className="space-y-5">
          {(() => {
            const ms = (plan.meals || []).map((m) => ({ ...m, __type: "meal" }));
            const ws = (plan.workouts || []).map((w) => ({ ...w, __type: "workout" }));
            const blocks = [...ms, ...ws].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
            return blocks.map((b) => {
              if (b.__type === "workout") {
                return (
                  <div
                    key={b.id}
                    data-testid={`client-workout-${b.id}`}
                    className="border-2 rounded-2xl p-5 sm:p-6 shadow-[0_2px_0_0_rgba(63,110,138,0.08)]"
                    style={{ borderColor: "#3F6E8A", background: "#EAF3F8" }}
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3">
                        <Barbell size={26} weight="duotone" style={{ color: "#3F6E8A" }} />
                        <div className="font-serif text-2xl sm:text-3xl leading-tight" style={{ color: "#2A4C63" }}>Allenamento</div>
                      </div>
                      <div
                        className="inline-flex items-center gap-1.5 text-sm sm:text-base font-medium px-2.5 py-1 rounded-full"
                        style={{ background: "rgba(255,255,255,0.65)", color: "#2A4C63" }}
                      >
                        <Clock size={15} weight="fill" style={{ color: "#3F6E8A" }} /> {b.time}
                      </div>
                    </div>
                    {b.text && (
                      <div className="mt-3 text-sm sm:text-base whitespace-pre-wrap" style={{ color: "#2A4C63" }}>
                        {b.text}
                      </div>
                    )}
                  </div>
                );
              }
              const m = b;
              return (
                <div
                  key={m.id}
                  className="border-2 border-[color:var(--text-primary)] rounded-2xl p-5 sm:p-6 bg-[color:var(--bg-primary)] shadow-[0_2px_0_0_rgba(26,26,24,0.06)]"
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="font-serif text-2xl sm:text-3xl leading-tight text-[color:var(--text-primary)]">{m.name}</div>
                <div
                  className="inline-flex items-center gap-1.5 text-sm sm:text-base font-medium px-2.5 py-1 rounded-full"
                  style={{ background: "var(--bg-secondary)", color: "var(--text-primary)" }}
                >
                  <Clock size={15} weight="fill" style={{ color: "var(--accent)" }} /> {m.time}
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  ["proteici", "Proteici", "#A65D47"],
                  ["amidacei", "Amidacei", "#8B7355"],
                  ["frutta", "Frutta", "#B08C4D"],
                  ["lipidici", "Lipidici", "#63705B"],
                ].map(([k, label, color]) => {
                  const n = m.portions?.[k] || 0;
                  return (
                    <div
                      key={k}
                      className="border-2 border-[color:var(--border)] rounded-xl p-3 sm:p-4"
                      style={{ background: "var(--bg-secondary)" }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-[11px] sm:text-xs uppercase tracking-widest font-semibold text-[color:var(--text-primary)]">{label}</div>
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                      </div>
                      <div className="mt-2 font-sans text-3xl sm:text-4xl font-semibold tabular-nums leading-none text-[color:var(--text-primary)]">
                        {String(n).replace(".", ",")}
                        <span className="text-xs ml-1.5 text-[color:var(--text-secondary)] font-medium">porz.</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {m.extras && (
                <div
                  className="mt-4 flex items-start gap-2 text-sm rounded-lg px-3 py-2"
                  style={{ background: "rgba(166,93,71,0.08)", color: "var(--text-primary)" }}
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                    style={{ background: "var(--accent)", color: "#FCFAF8", flexShrink: 0, marginTop: 1 }}>
                    Extra
                  </span>
                  <span className="italic">{m.extras}</span>
                </div>
              )}
              {(m.extras_items || []).length > 0 && (
                <ul
                  className="mt-4 rounded-lg px-3 py-2 space-y-1 text-sm"
                  style={{ background: "rgba(166,93,71,0.08)", color: "var(--text-primary)" }}
                  data-testid={`meal-extras-items-${m.id}`}
                >
                  {(m.extras_items || []).map((it) => (
                    <li key={it.id} className="flex items-start gap-2">
                      <span className="text-[color:var(--accent)] mt-0.5">•</span>
                      <span>{it.name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
              );
            });
          })()}
        </div>
        <div className="space-y-6">
          <MacrosSummary
            totals={plan.totals || {}}
            massaMagraKg={latestMeasurement?.massa_magra_kg || null}
            proteineNobiliG={proteineNobili}
            tdee={computeBmiTdee({
              pesoKg: latestMeasurement?.peso_kg || 0,
              heightCm: user?.height_cm || 0,
              massaMagraKg: latestMeasurement?.massa_magra_kg || 0,
              activityLevel: user?.activity_level || null,
            }).tdee}
          />
        </div>
      </div>
    </div>
  );
}
